import { useState } from 'react';
import {
  Dialog, DialogContent, TextField, Button, Typography, Alert, Stack, Link, Box, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Login modal shown over the public portal. On success, routes to the
// user's role home. "Register" navigates to the full registration page.
export default function LoginModal({ open, onClose }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const user = await login(email, password);
      const dest = user.role === 'VENDOR' ? '/vendor'
        : user.role === 'ADMIN' ? '/admin'
        : user.role === 'MDA_OFFICER' ? '/officer'
        : user.role === 'EVALUATOR' ? '/officer/evaluation'
        : user.role === 'AUDITOR' ? '/officer/audit' : '/portal';
      // Navigate only - don't also call the parent's onClose here. Some
      // callers (LoginRoute) wire onClose to navigate('/portal'), and
      // calling both raced two history pushes against each other, with
      // whichever fired last silently winning. The route change below
      // unmounts this dialog on its own.
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not sign in. Check your details and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, pr: 1 }}>
        <IconButton onClick={onClose} size="small" aria-label="close"><CloseIcon /></IconButton>
      </Box>
      <DialogContent sx={{ pt: 0, pb: 4, px: 4 }}>
        <Typography variant="h5" color="primary" gutterBottom>Sign in</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Access your procurement dashboard.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={submit}>
          <Stack spacing={2}>
            <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth autoFocus />
            <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required fullWidth />
            <Button type="submit" variant="contained" size="large" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </Stack>
        </form>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
          <Link component="button" type="button" variant="body2" onClick={() => { onClose?.(); navigate('/register'); }}>
            Register as vendor
          </Link>
          <Link component="button" type="button" variant="body2"
            onClick={() => setError('To reset your password, contact the procurement helpdesk on +234 903 069 0129 or support@taraba-eproc.gov.ng.')}>
            Forgot password?
          </Link>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}